import { useState, useMemo, Fragment } from 'react';
import { useQuery } from 'react-query';
import {
  BarChart3,
  AlertTriangle,
  Users,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  Wrench,
  Clock,
  RotateCcw,
  X,
  Copy
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { reportsAPI } from '@shared/apiClient';
import { formatCurrency } from '@shared/utils';
import toast from 'react-hot-toast';

const PRESET_RANGES = {
  sinceApril2026: 'Since Apr 2026',
  thisMonth: 'This Month',
  thisQuarter: 'This Quarter',
  ytd: 'Year to Date',
  lastYear: 'Last Year',
  custom: 'Custom Range'
};

const CLIENT_STACK = {
  customer: '#3b82f6',
  cspire: '#8b5cf6',
  franklin: '#059669'
};

const PIE_COLORS = ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#fef3c7'];

function getDateRange(preset) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  switch (preset) {
    case 'sinceApril2026':
      return {
        startDate: '2026-04-01',
        endDate: now.toISOString().split('T')[0]
      };
    case 'thisMonth':
      return {
        startDate: new Date(year, month, 1).toISOString().split('T')[0],
        endDate: now.toISOString().split('T')[0]
      };
    case 'thisQuarter': {
      const qStart = Math.floor(month / 3) * 3;
      return {
        startDate: new Date(year, qStart, 1).toISOString().split('T')[0],
        endDate: now.toISOString().split('T')[0]
      };
    }
    case 'ytd':
      return {
        startDate: new Date(year, 0, 1).toISOString().split('T')[0],
        endDate: now.toISOString().split('T')[0]
      };
    case 'lastYear':
      return {
        startDate: new Date(year - 1, 0, 1).toISOString().split('T')[0],
        endDate: new Date(year - 1, 11, 31).toISOString().split('T')[0]
      };
    default:
      return {
        startDate: new Date(year, 0, 1).toISOString().split('T')[0],
        endDate: now.toISOString().split('T')[0]
      };
  }
}

function StatCard({ icon: Icon, label, value, subValue, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-gray-500 truncate">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
        </div>
      </div>
    </div>
  );
}

function clientTypeLabel(t) {
  if (t === 'cspire') return 'C Spire';
  if (t === 'franklin') return 'Franklin';
  return 'Customer';
}

export default function TechMetrics() {
  const [preset, setPreset] = useState('sinceApril2026');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [sortBy, setSortBy] = useState('totalCompleted');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedTech, setExpandedTech] = useState(null);
  const [correctionsModalTech, setCorrectionsModalTech] = useState(null);

  const dateRange = useMemo(() => {
    if (preset === 'custom' && customStart && customEnd) {
      return { startDate: customStart, endDate: customEnd };
    }
    return getDateRange(preset);
  }, [preset, customStart, customEnd]);

  const { data, isLoading, isError } = useQuery(
    ['techMetrics', dateRange.startDate, dateRange.endDate],
    () => reportsAPI.getTechMetrics(dateRange),
    { keepPreviousData: true, staleTime: 60000 }
  );

  const { data: correctionsResp, isLoading: correctionsLoading } = useQuery(
    ['techCorrections', correctionsModalTech?.technicianId, dateRange.startDate, dateRange.endDate],
    () => reportsAPI.getTechCorrections({
      technicianId: correctionsModalTech.technicianId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    }),
    { enabled: !!correctionsModalTech }
  );

  const metrics = data?.data;
  const technicians = metrics?.technicians || [];
  const summary = metrics?.summary || {};
  const correctionsList = correctionsResp?.data?.corrections || [];

  const sortedTechs = useMemo(() => {
    const sorted = [...technicians];
    sorted.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === 'customerCalls') {
        aVal = a.completedByClientType?.customer ?? a.customerCalls ?? 0;
        bVal = b.completedByClientType?.customer ?? b.customerCalls ?? 0;
      } else if (sortBy === 'cspireCalls') {
        aVal = a.completedByClientType?.cspire ?? a.cspireCalls ?? 0;
        bVal = b.completedByClientType?.cspire ?? b.cspireCalls ?? 0;
      } else if (sortBy === 'franklinCalls') {
        aVal = a.completedByClientType?.franklin ?? a.franklinCalls ?? 0;
        bVal = b.completedByClientType?.franklin ?? b.franklinCalls ?? 0;
      }
      if (typeof aVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [technicians, sortBy, sortOrder]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-primary-600" />
      : <ArrowDown className="h-3.5 w-3.5 text-primary-600" />;
  };

  const barChartData = useMemo(() =>
    sortedTechs
      .filter(t => t.totalCompleted > 0)
      .map(t => {
        const c = t.completedByClientType || {};
        return {
          name: `${t.firstName} ${t.lastName.charAt(0)}.`,
          Customer: c.customer ?? t.customerCalls ?? 0,
          'C Spire': c.cspire ?? t.cspireCalls ?? 0,
          Franklin: c.franklin ?? t.franklinCalls ?? 0
        };
      }),
    [sortedTechs]
  );

  const correctionChartData = useMemo(() =>
    sortedTechs
      .filter(t => t.correctionCount > 0)
      .map(t => ({
        name: `${t.firstName} ${t.lastName.charAt(0)}.`,
        count: t.correctionCount
      })),
    [sortedTechs]
  );

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success('Service call ID copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-primary-600" />
            Technician Performance
          </h1>
          <p className="text-sm text-gray-500 mt-1">Review metrics to evaluate technician performance and make compensation decisions</p>
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-3 max-w-3xl">
            Labor hours and related totals reflect what is recorded on service calls and visits. Historical labor entries are not always revised when work is corrected later, so ranges before manual cleanup (for example before April 2026) can be skewed. Prefer the &quot;Since Apr 2026&quot; range when you need numbers aligned with cleaned-up data.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {Object.entries(PRESET_RANGES).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPreset(key)}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                preset === key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {preset === 'custom' && (
        <div className="flex items-center gap-3 mb-6 bg-white rounded-lg shadow p-4">
          <label className="text-sm font-medium text-gray-700">From</label>
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="input input-sm"
          />
          <label className="text-sm font-medium text-gray-700">To</label>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="input input-sm"
          />
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}

      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load metrics. Please try again.
        </div>
      )}

      {!isLoading && !isError && metrics && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={Wrench}
              label="Completed Calls"
              value={summary.totalCompleted?.toLocaleString() || '0'}
              subValue={`${summary.avgCallsPerWeek || 0}/week avg`}
              color="blue"
            />
            <StatCard
              icon={RotateCcw}
              label="Total Returns"
              value={summary.totalReturns?.toLocaleString() || '0'}
              color="purple"
            />
            <StatCard
              icon={AlertTriangle}
              label="Corrections"
              value={summary.totalCorrections?.toLocaleString() || '0'}
              color="amber"
            />
            <StatCard
              icon={Clock}
              label="Total Labor Hours"
              value={summary.totalLaborHours?.toLocaleString() || '0'}
              color="blue"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Completed calls by client type</h3>
              {barChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Customer" stackId="a" fill={CLIENT_STACK.customer} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="C Spire" stackId="a" fill={CLIENT_STACK.cspire} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Franklin" stackId="a" fill={CLIENT_STACK.franklin} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 text-center py-12">No data for selected period</p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Corrections by tech (count)</h3>
              {correctionChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={correctionChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="count"
                      nameKey="name"
                      label={({ name, count }) => `${name}: ${count}`}
                      labelLine={false}
                    >
                      {correctionChartData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value, 'Corrections']} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-[300px] text-gray-400">
                  <AlertTriangle className="h-8 w-8 mb-2" />
                  <p className="text-sm">No corrections in this period</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500" />
                Technician Breakdown
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-8 px-4 py-3"></th>
                    {[
                      { key: 'lastName', label: 'Technician' },
                      { key: 'totalCompleted', label: 'Completed' },
                      { key: 'customerCalls', label: 'Customer' },
                      { key: 'cspireCalls', label: 'C Spire' },
                      { key: 'franklinCalls', label: 'Franklin' },
                      { key: 'avgCallsPerWeek', label: 'Avg/Week' },
                      { key: 'returnCount', label: 'Returns' },
                      { key: 'returnRate', label: 'Return %' },
                      { key: 'correctionCount', label: 'Corrections' },
                      { key: 'avgLaborHoursPerCall', label: 'Avg Hrs/Call' },
                      { key: 'totalPartsCost', label: 'Parts Cost' }
                    ].map(({ key, label }) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      >
                        <div className="flex items-center gap-1">
                          {label}
                          <SortIcon col={key} />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedTechs.map((tech) => {
                    const isExpanded = expandedTech === tech.technicianId;
                    const typeEntries = Object.entries(tech.callsByType || {}).sort((a, b) => b[1] - a[1]);
                    const c = tech.completedByClientType || {};

                    return (
                      <Fragment key={tech.technicianId}>
                        <tr
                          className={`hover:bg-gray-50 cursor-pointer ${!tech.isActive ? 'opacity-60' : ''}`}
                          onClick={() => setExpandedTech(isExpanded ? null : tech.technicianId)}
                        >
                          <td className="px-4 py-3">
                            {isExpanded
                              ? <ChevronDown className="h-4 w-4 text-gray-400" />
                              : <ChevronRight className="h-4 w-4 text-gray-400" />}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                            {tech.firstName} {tech.lastName}
                            {!tech.isActive && <span className="ml-1.5 text-xs text-gray-400">(inactive)</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-semibold">{tech.totalCompleted}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{c.customer ?? tech.customerCalls ?? 0}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{c.cspire ?? tech.cspireCalls ?? 0}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{c.franklin ?? tech.franklinCalls ?? 0}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{tech.avgCallsPerWeek}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{tech.returnCount}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`${tech.returnRate > 10 ? 'text-red-600 font-semibold' : tech.returnRate > 5 ? 'text-amber-600' : 'text-gray-900'}`}>
                              {tech.returnRate}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {tech.correctionCount > 0 ? (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-amber-700 font-medium hover:text-amber-900 hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCorrectionsModalTech(tech);
                                }}
                              >
                                <AlertTriangle className="h-3.5 w-3.5" />
                                {tech.correctionCount}
                              </button>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{tech.avgLaborHoursPerCall}h</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(tech.totalPartsCost)}</td>
                        </tr>

                        {isExpanded && (
                          <tr key={`${tech.technicianId}-detail`} className="bg-gray-50">
                            <td colSpan={12} className="px-8 py-4">
                              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Calls by client type</p>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Customer</span>
                                      <span className="font-medium">{c.customer ?? tech.customerCalls ?? 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">C Spire</span>
                                      <span className="font-medium">{c.cspire ?? tech.cspireCalls ?? 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Franklin</span>
                                      <span className="font-medium">{c.franklin ?? tech.franklinCalls ?? 0}</span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Calls by Type</p>
                                  {typeEntries.length > 0 ? (
                                    <div className="space-y-1">
                                      {typeEntries.map(([typeName, count]) => (
                                        <div key={typeName} className="flex items-center justify-between text-sm">
                                          <span className="text-gray-700">{typeName}</span>
                                          <span className="font-medium text-gray-900">{count}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-400">No type data</p>
                                  )}
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Efficiency</p>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Total Labor Hours</span>
                                      <span className="font-medium">{tech.totalLaborHours}h</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Avg Hours/Call</span>
                                      <span className="font-medium">{tech.avgLaborHoursPerCall}h</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Avg Calls/Week</span>
                                      <span className="font-medium">{tech.avgCallsPerWeek}</span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Issues</p>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Returns</span>
                                      <span className="font-medium">{tech.returnCount}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Return Rate</span>
                                      <span className={`font-medium ${tech.returnRate > 10 ? 'text-red-600' : ''}`}>{tech.returnRate}%</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Corrections (at fault)</span>
                                      <span className={`font-medium ${tech.correctionCount > 0 ? 'text-amber-600' : ''}`}>{tech.correctionCount}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {sortedTechs.length === 0 && (
                    <tr>
                      <td colSpan={12} className="px-4 py-8 text-center text-sm text-gray-400">
                        No technician data for the selected period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {summary.dateRange && (
            <p className="text-xs text-gray-400 mt-4 text-right">
              Data from {new Date(summary.dateRange.startDate).toLocaleDateString()} to {new Date(summary.dateRange.endDate).toLocaleDateString()} ({summary.dateRange.weeks} weeks)
            </p>
          )}

          {correctionsModalTech && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
              <div
                className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col"
                role="dialog"
                aria-modal="true"
                aria-labelledby="corrections-modal-title"
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                  <h2 id="corrections-modal-title" className="text-lg font-semibold text-gray-900">
                    Corrections — {correctionsModalTech.firstName} {correctionsModalTech.lastName}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setCorrectionsModalTech(null)}
                    className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="overflow-auto px-5 py-4 flex-1">
                  {correctionsLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                    </div>
                  ) : correctionsList.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No correction records for this period.</p>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase">
                          <th className="py-2 pr-3">Completed</th>
                          <th className="py-2 pr-3">Client</th>
                          <th className="py-2 pr-3">Type</th>
                          <th className="py-2 pr-3">Performed by</th>
                          <th className="py-2 pr-3">Note</th>
                          <th className="py-2 pr-3">Call ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {correctionsList.map((row) => (
                          <tr key={row.serviceCallId}>
                            <td className="py-2 pr-3 whitespace-nowrap text-gray-900">
                              {row.completedDate ? new Date(row.completedDate).toLocaleDateString() : '—'}
                            </td>
                            <td className="py-2 pr-3 text-gray-800">{row.clientLabel}</td>
                            <td className="py-2 pr-3 text-gray-600">{clientTypeLabel(row.clientType)}</td>
                            <td className="py-2 pr-3 text-gray-800">
                              {row.performedBy
                                ? `${row.performedBy.firstName} ${row.performedBy.lastName}`
                                : '—'}
                            </td>
                            <td className="py-2 pr-3 text-gray-700 max-w-xs">{row.correctionNote || '—'}</td>
                            <td className="py-2 pr-3">
                              <div className="flex items-center gap-1 font-mono text-xs text-gray-600">
                                <span className="truncate max-w-[120px]" title={row.serviceCallId}>
                                  {row.serviceCallId}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => copyId(row.serviceCallId)}
                                  className="p-1 rounded text-gray-500 hover:bg-gray-100"
                                  title="Copy ID"
                                >
                                  <Copy className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
